import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { InternalPage, ResetFormHandler } from 'theSource/components';
import {
  ResponseDto,
  ErrorResponseDto,
  FormValuesDto,
  FormValues,
} from 'theSource/types';
import { mapFormValues, useApiMutation } from 'theSource/utils';
import {
  GET_DEPENDENTS_QUERY_KEY,
  mapDependentsModel,
  stripErrorMessage,
} from '../utils';
import { MyFamilyMemberForm } from '../components';
import { SAVE_NEW_DEPENDENT_QUERY_KEY } from './utils';
import { DependentsResponseDto } from '../types';
import { ROUTES } from 'theSource/app/routes';
import { BackLink } from 'enrollment/components';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';

interface MyFamilyMembersAddNewPageProps {
  data: DependentsResponseDto;
}

export const MyFamilyMembersAddNewPage = ({
  data,
}: MyFamilyMembersAddNewPageProps) => {
  const navigate = useNavigate();

  const enrollmentContextType = useSelector(selectEnrollmentContext);

  const { isLoading: isSaving, mutate } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    FormValuesDto
  >({
    queryKey: SAVE_NEW_DEPENDENT_QUERY_KEY,
    queryKeysToInvalidate: `${GET_DEPENDENTS_QUERY_KEY}-${enrollmentContextType}`,
    invalidateQueriesCallback: () => navigateBack(),
    method: 'post',
    endpoint: '/dependents',
    getErrorMessage: (error) =>
      stripErrorMessage(error.response?.data?.details),
  });

  const categories = useMemo(() => mapDependentsModel(data), [data]);

  const navigateBack = () => navigate(ROUTES.additional.profile.myFamily.path);

  const handleSubmit = (values: FormValues, resetForm: ResetFormHandler) =>
    mutate(mapFormValues(categories, values), {
      onSuccess: () => resetForm(),
    });

  const handleCancelClick = () => navigateBack();

  return (
    <InternalPage
      testId="family-members-add-new-page"
      lookupCode="enroll_dependents/new"
      CustomActionComponent={<BackLink />}
      customActionAlignment="left"
    >
      <MyFamilyMemberForm
        categories={categories}
        isLoading={isSaving}
        onSubmitClick={handleSubmit}
        onCancelClick={handleCancelClick}
      />
    </InternalPage>
  );
};
